/**
 * Hidden gallery of real REDI components, for a design-system reference page
 * fed by scripts/capture-design-system.mjs. Never linked from the app —
 * reachable only in a local dev build (__DEV__) or a build where the same
 * `redi:auditFixtures` localStorage flag the web audits set is present (see
 * scripts/lib/web-audit-helpers.mjs's createNewPage). Everyone else gets the
 * same not-found screen as a mistyped URL.
 *
 * Every specimen below is a REAL component with REAL props — nothing here
 * is restyled to look a certain way. Fidelity to how the app actually
 * renders is the whole point: a screenshot of this page stands in for the
 * real screen a specimen normally lives on.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import { Stack } from 'expo-router';
import { Download, MapPin, Globe, Bell, Settings } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  FONT_SIZES,
  ELEVATION,
} from '../../src/theme';
import {
  CONTENT_MAX_WIDTH,
  NARROW_MAX_WIDTH,
  SIDEBAR_WIDTH,
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_FOOTER_HEIGHT,
  DETAIL_PANEL_WIDTH,
  PRESENTATION_PANEL_WIDTH,
  PLAN_PANEL_WIDTH,
  DESKTOP_CONTENT_MAX_WIDTH,
  READING_MAX_WIDTH,
  SHELL_HEADER_HEIGHT,
  SHELL_RULE_WIDTH,
  DOCK_PANEL_TRANSITION_MS,
  MIN_MAP_WIDTH,
  COMPANION_PANEL_WIDTH,
  DOCK_TAB_WIDTH,
} from '../../src/theme/layout';
import { lightColors, darkColors } from '../../src/theme/colors';
import { Z_INDEX } from '../../src/theme/zIndex';
import { Button, Input, DotLabel, ListItem, EmptyState, Card } from '@components/common';
import { PanelHeader } from '@components/layout/PanelHeader';
import { SegmentedControl } from '@components/common/SegmentedControl';
import { DefensePlanFeatureRow } from '@components/map/DefensePlanFeatureRow.web';
import type { DefensePlanFeature, PlanVisibility } from '../../src/types';
import { VisibilityPill } from '@components/map/DefensePlanPanel.web';
import NotFoundScreen from '../+not-found';

// Same key/value web-audit-helpers.mjs's createNewPage sets before every
// navigation (`redi:auditFixtures` = '1') — this is how the capture script
// (running against the built `dist` bundle, where __DEV__ is false) gets
// past this gate. Kept as a plain string comparison, not truthiness alone,
// so a stray empty-string value some other code path might set doesn't
// accidentally open the gallery.
const AUDIT_FIXTURES_KEY = 'redi:auditFixtures';

function auditFixturesFlagSet(): boolean {
  if (Platform.OS !== 'web') return false;
  try {
    return typeof localStorage !== 'undefined' && !!localStorage.getItem(AUDIT_FIXTURES_KEY);
  } catch {
    return false;
  }
}

function galleryAllowed(): boolean {
  if (typeof __DEV__ !== 'undefined' && __DEV__) return true;
  return auditFixturesFlagSet();
}

// A fake DefensePlanFeature for DefensePlanFeatureRow — the row is a pure
// presentational component (no store reads), so a hand-built fixture is
// enough to render it exactly as it renders in the real plan editor.
// `createdAt` is never read by the row or by formatFeatureMeta, so it's
// stubbed rather than importing the Firestore Timestamp constructor (which
// CLAUDE.md's layering rule bans from components/screens).
const SAMPLE_FEATURE: DefensePlanFeature = {
  id: 'ds-sample-feature',
  strategy: 'fire-break',
  kind: 'line',
  coords: [
    { lat: 34.2439, lng: -116.9114 },
    { lat: 34.2451, lng: -116.9098 },
  ],
  createdAt: { toMillis: () => Date.now() } as unknown as DefensePlanFeature['createdAt'],
};

function Specimen({
  testID,
  style,
  children,
}: {
  testID: string;
  style?: object;
  children: ReactNode;
}) {
  return (
    <View testID={testID} style={[styles.specimen, style]}>
      {children}
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionHeading, { color: colors.text }]}>{title}</Text>
      <View style={styles.specimenRow}>{children}</View>
    </View>
  );
}

function ButtonGroup() {
  return (
    <Section title="Button">
      <Specimen testID="ds-button-size-xs">
        <Button title="Save" size="xs" variant="primary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-size-sm">
        <Button title="Save" size="sm" variant="primary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-size-md">
        <Button title="Save" size="md" variant="primary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-size-lg">
        <Button title="Save" size="lg" variant="primary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-primary">
        <Button title="Save" size="md" variant="primary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-secondary">
        <Button title="Cancel" size="md" variant="secondary" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-outline">
        <Button title="Cancel" size="md" variant="outline" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-ghost">
        <Button title="Dismiss" size="md" variant="ghost" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-danger">
        <Button title="Delete" size="md" variant="danger" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-variant-progress">
        <Button title="Saving…" size="md" variant="progress" onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-icon-left">
        <Button
          title="Download"
          size="md"
          variant="secondary"
          leftIcon={Download}
          onPress={() => {}}
        />
      </Specimen>
      <Specimen testID="ds-button-icon-right">
        <Button
          title="Show on map"
          size="md"
          variant="secondary"
          rightIcon={MapPin}
          onPress={() => {}}
        />
      </Specimen>
      <Specimen testID="ds-button-disabled">
        <Button title="Save" size="md" variant="primary" disabled onPress={() => {}} />
      </Specimen>
      <Specimen testID="ds-button-loading">
        <Button title="Save" size="md" variant="primary" loading onPress={() => {}} />
      </Specimen>
    </Section>
  );
}

function InputGroup() {
  return (
    <Section title="Input">
      <Specimen testID="ds-input-default">
        <View style={styles.inputWidth}>
          <Input label="Email" placeholder="name@example.com" />
        </View>
      </Specimen>
      <Specimen testID="ds-input-error">
        <View style={styles.inputWidth}>
          <Input label="Email" placeholder="name@example.com" error="Enter a valid email address" />
        </View>
      </Specimen>
      <Specimen testID="ds-input-hint">
        <View style={styles.inputWidth}>
          <Input
            label="Email"
            placeholder="name@example.com"
            hint="We'll only use this to send updates"
          />
        </View>
      </Specimen>
      <Specimen testID="ds-input-compact">
        <View style={styles.inputWidth}>
          <Input compact label="Volunteer name" placeholder="e.g. Jane Doe" />
        </View>
      </Specimen>
      <Specimen testID="ds-input-compact-empty">
        <View style={styles.inputWidth}>
          <Input compact label="Notes" />
        </View>
      </Specimen>
    </Section>
  );
}

function DotLabelGroup() {
  const variants = ['progress', 'risk', 'news', 'partial', 'primary', 'muted'] as const;
  const labels: Record<(typeof variants)[number], string> = {
    progress: 'Progress',
    risk: 'Opportunity',
    news: 'News',
    partial: 'Partial',
    primary: 'Primary',
    muted: 'Unverified',
  };
  return (
    <Section title="DotLabel">
      {variants.map((variant) => (
        <Specimen key={variant} testID={`ds-dotlabel-${variant}`}>
          <DotLabel label={labels[variant]} variant={variant} />
        </Specimen>
      ))}
    </Section>
  );
}

function SegmentedControlGroup() {
  return (
    <Section title="SegmentedControl">
      <Specimen testID="ds-segmentedcontrol-3option">
        <SegmentedControl
          options={[
            { key: 'line', label: 'Line' },
            { key: 'point', label: 'Point' },
            { key: 'area', label: 'Area' },
          ]}
          value="point"
          onChange={() => {}}
          ariaLabel="Draw as"
          testID="ds-segmented-3"
        />
      </Specimen>
      <Specimen testID="ds-segmentedcontrol-2option">
        <SegmentedControl
          options={[
            { key: 'shared', label: 'Shared' },
            { key: 'private', label: 'Private' },
          ]}
          value="shared"
          onChange={() => {}}
          ariaLabel="Who can see this plan"
          testID="ds-segmented-2"
        />
      </Specimen>
    </Section>
  );
}

function PanelHeaderGroup() {
  return (
    <Section title="PanelHeader">
      <Specimen testID="ds-panelheader-default" style={{ width: DETAIL_PANEL_WIDTH }}>
        <PanelHeader
          title="11 Polique Canyon Road"
          onClose={() => {}}
          testID="ds-panelheader-close"
        />
      </Specimen>
    </Section>
  );
}

function PopoverGroup() {
  const [visibility, setVisibility] = useState<PlanVisibility>('shared');
  return (
    <Section title="Popover">
      {/* The plan header's real Shared/Private pill, mounted open. The menu
          portals to document.body, so the capture script finds it by its
          testID, plan-visibility-menu. */}
      <Specimen testID="ds-popover-trigger">
        <VisibilityPill value={visibility} onChange={setVisibility} initialOpen />
      </Specimen>
    </Section>
  );
}

function ListItemGroup() {
  return (
    <Section title="ListItem">
      <Specimen testID="ds-listitem-plain" style={styles.listItemWidth}>
        <ListItem
          icon={Bell}
          label="Notifications"
          onPress={() => {}}
          testID="ds-listitem-plain-row"
        />
      </Specimen>
      <Specimen testID="ds-listitem-value" style={styles.listItemWidth}>
        <ListItem
          icon={Globe}
          label="Theme"
          value="System"
          onPress={() => {}}
          testID="ds-listitem-value-row"
        />
      </Specimen>
      <Specimen testID="ds-listitem-switch" style={styles.listItemWidth}>
        <ListItem
          icon={Settings}
          label="Push notifications"
          toggle
          toggleValue
          onToggle={() => {}}
          testID="ds-listitem-switch-row"
        />
      </Specimen>
      <Specimen testID="ds-listitem-chevron" style={styles.listItemWidth}>
        <ListItem
          icon={MapPin}
          label="Saved places"
          badge="3"
          onPress={() => {}}
          testID="ds-listitem-chevron-row"
        />
      </Specimen>
      <Specimen testID="ds-planfeaturerow-idle" style={{ width: PLAN_PANEL_WIDTH }}>
        <DefensePlanFeatureRow feature={SAMPLE_FEATURE} selected={false} onSelect={() => {}} />
      </Specimen>
      <Specimen testID="ds-planfeaturerow-selected" style={{ width: PLAN_PANEL_WIDTH }}>
        <DefensePlanFeatureRow feature={SAMPLE_FEATURE} selected onSelect={() => {}} />
      </Specimen>
    </Section>
  );
}

function EmptyStateGroup() {
  return (
    <Section title="EmptyState">
      <Specimen testID="ds-emptystate-default">
        <EmptyState
          title="No defenses yet"
          message="Draw your first defense on the map to start this plan."
          actionLabel="Add a defense"
          onAction={() => {}}
        />
      </Specimen>
      <Specimen testID="ds-emptystate-compact">
        <EmptyState
          compact
          title="No updates yet"
          message="Updates from volunteers will show up here."
          actionLabel="Ask for an update"
          onAction={() => {}}
        />
      </Specimen>
    </Section>
  );
}

function CardGroup() {
  return (
    <Section title="Card">
      <Specimen testID="ds-card-variant-outlined">
        <Card variant="outlined">
          <Text style={styles.cardText}>Outlined card</Text>
        </Card>
      </Specimen>
      <Specimen testID="ds-card-variant-filled">
        <Card variant="filled">
          <Text style={styles.cardText}>Filled card</Text>
        </Card>
      </Specimen>
      <Specimen testID="ds-card-padding-sm">
        <Card variant="outlined" padding="sm">
          <Text style={styles.cardText}>Padding sm</Text>
        </Card>
      </Specimen>
      <Specimen testID="ds-card-padding-md">
        <Card variant="outlined" padding="md">
          <Text style={styles.cardText}>Padding md</Text>
        </Card>
      </Specimen>
      <Specimen testID="ds-card-padding-lg">
        <Card variant="outlined" padding="lg">
          <Text style={styles.cardText}>Padding lg</Text>
        </Card>
      </Specimen>
    </Section>
  );
}

// The two rows a plain footer or feature row uses at xs — copied literally
// from DefensePlanPanel.web.tsx's own `footerActions`/`footerAction` styles
// so the pattern reads exactly like the real footer, not an approximation.
const footerActions = { flexDirection: 'row' as const, gap: SPACING.sm };
const footerAction = { flex: 1 };

// DefensePlanPanel.web.tsx's own sectionLabelSpaced rhythm: 24px above a
// section label, 8px from the label to its content, no top margin on the
// first section in a scrolling body.
const sectionLabelSpaced = { marginTop: SPACING.lg, marginBottom: SPACING.sm };
const sectionLabelFirst = { marginTop: 0 };

function PatternsGroup() {
  const { colors } = useTheme();
  return (
    <Section title="Patterns">
      <Specimen testID="ds-pattern-footer-halves" style={{ width: PLAN_PANEL_WIDTH }}>
        <View style={footerActions}>
          <Button
            title="Cancel"
            size="sm"
            variant="secondary"
            style={footerAction}
            onPress={() => {}}
          />
          <Button
            title="Save"
            size="sm"
            variant="primary"
            style={footerAction}
            onPress={() => {}}
          />
        </View>
      </Specimen>

      <Specimen testID="ds-pattern-row-actions" style={{ width: PLAN_PANEL_WIDTH }}>
        <View style={styles.rowActions}>
          <Text style={[styles.rowActionsLabel, { color: colors.text }]}>Fire break</Text>
          <Button title="Assign" size="xs" variant="secondary" onPress={() => {}} />
        </View>
      </Specimen>

      <Specimen testID="ds-pattern-labels" style={{ width: PLAN_PANEL_WIDTH }}>
        <View style={styles.patternLabelsStack}>
          <Input compact label="Volunteer name" placeholder="e.g. Jane Doe" />
          <Input compact label="Notes" />
        </View>
      </Specimen>

      <Specimen testID="ds-pattern-section-rhythm" style={{ width: PLAN_PANEL_WIDTH }}>
        <View>
          <Text
            style={[
              styles.overlineLabel,
              sectionLabelSpaced,
              sectionLabelFirst,
              { color: colors.textSecondary },
            ]}>
            Add a defense
          </Text>
          <Text style={[styles.bodyText, { color: colors.text }]}>Palette content goes here.</Text>
          <Text style={[styles.overlineLabel, sectionLabelSpaced, { color: colors.textSecondary }]}>
            Defenses
          </Text>
          <Text style={[styles.bodyText, { color: colors.text }]}>Feature list goes here.</Text>
        </View>
      </Specimen>

      <Specimen testID="ds-pattern-selected-row" style={{ width: PLAN_PANEL_WIDTH }}>
        <View>
          <View style={[styles.selectedRow, { backgroundColor: 'transparent' }]}>
            <Text style={[styles.bodyText, { color: colors.text }]}>Fire break</Text>
          </View>
          <View style={[styles.selectedRow, { backgroundColor: colors.pressedSurface }]}>
            <Text style={[styles.bodyText, { color: colors.text }]}>Tree thinning</Text>
          </View>
          <View style={[styles.selectedRow, { backgroundColor: 'transparent' }]}>
            <Text style={[styles.bodyText, { color: colors.text }]}>Water tank</Text>
          </View>
        </View>
      </Specimen>

      <Specimen testID="ds-pattern-button-icons" style={{ width: PLAN_PANEL_WIDTH }}>
        <View style={styles.buttonIconsStack}>
          <Button
            title="Download"
            size="sm"
            variant="secondary"
            leftIcon={Download}
            onPress={() => {}}
          />
          <Button
            title="Show on map"
            size="sm"
            variant="secondary"
            rightIcon={MapPin}
            onPress={() => {}}
          />
          <Button title="View details" size="sm" variant="secondary" onPress={() => {}} />
        </View>
      </Specimen>
    </Section>
  );
}

function DesignSystemGallery() {
  const { colors } = useTheme();

  // Exposes the theme barrel's raw tokens on `window` so the capture script
  // (and, downstream, a design-system reference page) can read the exact
  // values every component above was styled with, instead of a second,
  // possibly-drifting copy. Web only, and only ever read by tooling — no
  // production code depends on this global.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    try {
      (window as unknown as { __rediTokens?: unknown }).__rediTokens = {
        light: lightColors,
        dark: darkColors,
        SPACING,
        BORDER_RADIUS,
        TYPOGRAPHY,
        FONT_SIZES,
        ELEVATION,
        layout: {
          CONTENT_MAX_WIDTH,
          NARROW_MAX_WIDTH,
          SIDEBAR_WIDTH,
          SIDEBAR_RAIL_WIDTH,
          SIDEBAR_FOOTER_HEIGHT,
          DETAIL_PANEL_WIDTH,
          PRESENTATION_PANEL_WIDTH,
          PLAN_PANEL_WIDTH,
          DESKTOP_CONTENT_MAX_WIDTH,
          READING_MAX_WIDTH,
          SHELL_HEADER_HEIGHT,
          SHELL_RULE_WIDTH,
          DOCK_PANEL_TRANSITION_MS,
          MIN_MAP_WIDTH,
          COMPANION_PANEL_WIDTH,
          DOCK_TAB_WIDTH,
        },
        Z_INDEX,
      };
    } catch {
      // Non-web or a locked-down runtime — nothing downstream depends on
      // this global existing.
    }
  }, []);

  return (
    <>
      <Stack.Screen options={{ title: 'Design System' }} />
      <ScrollView
        style={[styles.root, { backgroundColor: colors.background }]}
        contentContainerStyle={styles.content}
        testID="design-system-gallery">
        <Text style={[styles.pageTitle, { color: colors.text }]}>Design system</Text>
        <ButtonGroup />
        <InputGroup />
        <DotLabelGroup />
        <SegmentedControlGroup />
        <PanelHeaderGroup />
        {/* CompanionPanel and DefensePlanPanel's own header are skipped —
            see the report in the change that added this page. Both mount
            through DockedPanel, which fixed-positions itself against the
            viewport (dock-inset registration, offset/width driven by
            uiStore) rather than flowing in a normal document — outside the
            map shell they'd render as a full-height overlay across this
            scrolling page, not a croppable specimen. */}
        <PopoverGroup />
        <ListItemGroup />
        <EmptyStateGroup />
        <CardGroup />
        <PatternsGroup />
      </ScrollView>
    </>
  );
}

export default function DesignSystemRoute() {
  if (!galleryAllowed()) return <NotFoundScreen />;
  return <DesignSystemGallery />;
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  content: {
    maxWidth: 960,
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.lg,
    gap: SPACING.xl,
  },
  pageTitle: {
    ...TYPOGRAPHY.h2,
  },
  section: {
    gap: SPACING.sm,
  },
  sectionHeading: {
    ...TYPOGRAPHY.overline,
  },
  specimenRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: SPACING.lg,
  },
  specimen: {
    alignSelf: 'flex-start',
    padding: SPACING.sm,
  },
  inputWidth: {
    width: 280,
  },
  listItemWidth: {
    width: 320,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
    overflow: 'hidden',
  },
  cardText: {
    ...TYPOGRAPHY.body,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  rowActionsLabel: {
    ...TYPOGRAPHY.label,
  },
  patternLabelsStack: {
    gap: SPACING.sm,
  },
  overlineLabel: {
    ...TYPOGRAPHY.overline,
  },
  bodyText: {
    ...TYPOGRAPHY.body,
  },
  selectedRow: {
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  buttonIconsStack: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
});
